import React from 'react';
import { useBattle } from '../context';
import Card from '../../components/Card';

import type { PlayerNumber } from '../../types/player';

/**
 * SetsunaModal コンポーネント
 *
 * 刹那詠唱カード選択モーダル
 * - 相手ターン中に刹那詠唱モードがアクティブな時に表示
 * - 発動可能な刹那詠唱カードを一覧表示
 * - カード選択と発動を行う
 */
const SetsunaModal: React.FC = () => {
  const {
    currentPlayer,
    isMobile,
    setsuna,
  } = useBattle();

  // 刹那詠唱関連を展開
  const {
    setsunaPendingActivation,
    setsunaPendingCard,
    cancelSetsunaMagicMode,
    selectSetsunaMagicCard,
    activateSetsunaMagic,
    getSetsunaMagicsForNonActivePlayer,
    getSetsunaActualCost,
  } = setsuna;

  // 刹那詠唱の発動プレイヤー（現在プレイヤーではない方）
  const setsunaActivatingPlayer: PlayerNumber = currentPlayer === 1 ? 2 : 1;

  // 刹那詠唱モードでない場合は表示しない
  if (!setsunaPendingActivation) return null;

  // 発動可能なカードを取得
  const activatableCards = getSetsunaMagicsForNonActivePlayer();

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
      }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #1a1a3a 0%, #2a1a4a 100%)',
          borderRadius: '16px',
          padding: isMobile ? '16px' : '24px',
          maxWidth: '600px',
          width: '90vw',
          maxHeight: '80vh',
          overflow: 'auto',
          border: '3px solid #6b4ce6',
          boxShadow: '0 0 40px rgba(107,76,230,0.5)',
        }}
      >
        {/* ヘッダー */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}>
          <h2 style={{
            margin: 0,
            color: '#a78bfa',
            fontSize: isMobile ? '1.2rem' : '1.5rem',
            textShadow: '0 0 10px rgba(167,139,250,0.5)',
          }}>
            ⚡ 刹那詠唱
          </h2>
          <button
            onClick={cancelSetsunaMagicMode}
            style={{
              padding: '8px 16px',
              background: 'rgba(255,107,107,0.2)',
              border: '1px solid #ff6b6b',
              borderRadius: '8px',
              color: '#ff6b6b',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            ✕ キャンセル
          </button>
        </div>

        {/* 説明 */}
        <div style={{
          marginBottom: '16px',
          color: '#aaa',
          fontSize: isMobile ? '12px' : '14px',
        }}>
          発動するカードを選択してください
        </div>

        {/* カード一覧 */}
        {activatableCards.length > 0 ? (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            justifyContent: 'center',
            marginBottom: '16px',
          }}>
            {activatableCards.map(card => (
              <div
                key={card.uniqueId}
                onClick={() => selectSetsunaMagicCard(card)}
                style={{
                  cursor: 'pointer',
                  transform: setsunaPendingCard?.uniqueId === card.uniqueId ? 'scale(1.05)' : 'scale(1)',
                  transition: 'transform 0.2s ease',
                }}
              >
                <Card
                  card={card}
                  selected={setsunaPendingCard?.uniqueId === card.uniqueId}
                  small={isMobile}
                />
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '24px',
            color: '#666',
          }}>
            発動可能なカードがありません
          </div>
        )}

        {/* 選択中のカード情報 */}
        {setsunaPendingCard && (
          <div style={{
            background: 'rgba(107,76,230,0.2)',
            border: '1px solid #6b4ce6',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px',
          }}>
            <div style={{
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: 'bold',
              color: '#ffd700',
              marginBottom: '8px',
            }}>
              選択中: {setsunaPendingCard.name}
            </div>
            <div style={{
              fontSize: isMobile ? '11px' : '13px',
              color: '#ccc',
              lineHeight: 1.5,
            }}>
              {setsunaPendingCard.effect}
            </div>
          </div>
        )}

        {/* 発動ボタン */}
        {setsunaPendingCard && (
          <button
            onClick={() => activateSetsunaMagic(setsunaPendingCard)}
            style={{
              width: '100%',
              padding: isMobile ? '12px' : '16px',
              background: 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(107,76,230,0.4)',
            }}
          >
            ⚡ {setsunaPendingCard.name} を発動
            （コスト: {getSetsunaActualCost(setsunaPendingCard, setsunaActivatingPlayer || 1)}）
          </button>
        )}
      </div>
    </div>
  );
};

export default SetsunaModal;
